// Diagnostic Attempts table for the BMD report. Lists every
// `dt_diagnostics_session_topic` for the selected student and year level, in
// reverse-chronological order. Score renders as filled and empty circles plus
// an "N/M" caption. The status pill mapping is simplified: once the
// remediation flow lands, COMPLETED can split into FM and chain pending, and
// REMEDIATED can pick up "SB done".
//
// The session dropdown above the table filters rows client-side to a single
// `fk_diagnostics_session_key`, or "all". KPIs and the radar are deliberately
// not scoped by this filter; they always reflect latest-per-topic across the
// year level.

import React from "react"
import type { BmdAttemptHistoryRow } from "../types/bmd-report.types"
import { plainText } from "../utils/formatters"
import { useReportCopy, type ReportCopy } from "../services/report-copy"

const ALL_DATES = "__all__"

type DateOption = {
  // Plain date label, also used as the dropdown value (dates are unique).
  date: string
  ts: number
}

// Per-session metadata: which date it belongs to, plus, when that date holds
// more than one test, its 1-based ordinal (newest is Test 1) and the total for
// that date. The row reads this to render the small "Test N" pill that
// separates same-date sessions.
type SessionMeta = {
  dateLabel: string
  testIndex: number
  testTotal: number
}

function buildSessionIndex(attempts: BmdAttemptHistoryRow[]): {
  dateOptions: DateOption[]
  bySessionKey: Map<string, SessionMeta>
} {
  const latestByKey = new Map<string, number>()
  for (const a of attempts) {
    const key = a.fk_diagnostics_session_key
    if (!key) continue
    const t = a.attempted_on ? new Date(a.attempted_on).getTime() : NaN
    if (Number.isNaN(t)) continue
    const prev = latestByKey.get(key)
    if (prev == null || t > prev) latestByKey.set(key, t)
  }

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })

  type Bucket = { date: string; maxTs: number; entries: { key: string; ts: number }[] }
  const buckets = new Map<string, Bucket>()
  for (const [key, ts] of latestByKey.entries()) {
    const date = formatDate(ts)
    let b = buckets.get(date)
    if (!b) {
      b = { date, maxTs: ts, entries: [] }
      buckets.set(date, b)
    }
    b.entries.push({ key, ts })
    if (ts > b.maxTs) b.maxTs = ts
  }

  const dateOptions: DateOption[] = Array.from(buckets.values())
    .sort((a, b) => b.maxTs - a.maxTs)
    .map((b) => ({ date: b.date, ts: b.maxTs }))

  const bySessionKey = new Map<string, SessionMeta>()
  for (const b of buckets.values()) {
    b.entries.sort((a, c) => c.ts - a.ts)
    const total = b.entries.length
    b.entries.forEach((e, i) => {
      bySessionKey.set(e.key, {
        dateLabel: b.date,
        testIndex: i + 1,
        testTotal: total,
      })
    })
  }

  return { dateOptions, bySessionKey }
}

type StatusPill = {
  label: string
  bgClass: string
  textClass: string
}

// Splits the "pending remediation" pill into what the topic is actually
// waiting for. Set this to false and every topic goes back to the single
// "Pending remediation" pill, with no other edit needed. The two backend
// counts it reads are additive and can stay either way.
const SPLIT_PENDING_PILL = true

// Returns every status that applies, not just the most severe. A raised flag
// used to short-circuit and hide the topic's own state, so a topic that was
// both flagged and awaiting remediation showed only the flag and the tutor
// could not see there was outstanding work as well.
function statusPillsFor(
  row: BmdAttemptHistoryRow,
  // What an answered block is waiting for, which is per subject: maths owes an FM retry or a
  // prerequisite test, English owes nothing at all. See services/report-copy.tsx.
  copy: Pick<ReportCopy, "completedPill" | "splitCompletedPill">,
): StatusPill[] {
  const pills: StatusPill[] = []
  if (row.has_unresolved_flag) {
    pills.push({
      label: "Intervention needed",
      bgClass: "bg-danger",
      textClass: "text-white",
    })
  }
  switch (row.topic_status) {
    case "REMEDIATED":
      pills.push({
        label: "Passed",
        bgClass: "bg-success",
        textClass: "text-white",
      })
      break
    case "COMPLETED": {
      const fm = row.pending_fm_count ?? 0
      const chain = row.chain_owed_count ?? 0
      // Both can be outstanding at once: a fundamental misconception earns the
      // FM retry and a prerequisite chain.
      const label =
        !SPLIT_PENDING_PILL || !copy.splitCompletedPill || (fm === 0 && chain === 0)
          ? copy.completedPill
          : fm > 0 && chain > 0
            ? "Pending FM + prerequisite test"
            : fm > 0
              ? "Pending FM"
              : "Pending prerequisite test"
      pills.push({
        label,
        // An answered English block owes nothing, so its pill is a statement rather than a
        // warning; maths is still waiting for remediation and keeps the amber.
        bgClass: copy.splitCompletedPill ? "bg-warning" : "bg-success",
        textClass: copy.splitCompletedPill ? "text-dark" : "text-white",
      })
      break
    }
    case "IN_PROGRESS":
      pills.push({
        label: "In progress",
        bgClass: "bg-info",
        textClass: "text-white",
      })
      break
    case "PENDING":
    default:
      pills.push({
        label: "Pending",
        bgClass: "bg-secondary",
        textClass: "text-white",
      })
      break
  }
  return pills
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

const ScoreCircles: React.FC<{
  correct: number
  total: number
  /**
   * Answers in this block a tutor has not marked yet. They are excluded from `total` rather
   * than counted wrong, so a block can read "2/5" when the student answered eight things - and
   * a score that quietly shrinks is worse than no score. Zero or absent draws nothing.
   */
  awaiting?: number
}> = ({ correct, total, awaiting = 0 }) => {
  if (total <= 0) {
    // Nothing settled, but something may still be with a tutor - which is a different thing
    // from an untouched block and reads as one without this.
    return awaiting > 0 ? (
      <span className="text-muted font-size-12">
        {awaiting} awaiting marking
      </span>
    ) : (
      <span className="text-muted">—</span>
    )
  }
  // Cap visible circles to keep the row from breaking on wide topics. The
  // text "N/M" is the source of truth.
  const cap = 12
  const visible = Math.min(total, cap)
  const correctVisible = Math.min(correct, visible)
  const circles = []
  for (let i = 0; i < visible; i++) {
    const filled = i < correctVisible
    circles.push(
      <span
        key={i}
        className="d-inline-block rounded-circle me-1"
        style={{
          width: 10,
          height: 10,
          backgroundColor: filled ? "#198754" : "#dee2e6",
        }}
      />,
    )
  }
  // The PERCENTAGE leads, because that is the number a tutor compares across
  // topics: "1/15" and "3/44" are not comparable at a glance, 7% and 7% are.
  // The fraction stays underneath as the raw count, and the dots stay because
  // they show WHICH questions went wrong at a glance.
  const pct = Math.round((correct / total) * 100)
  // Traffic-light on the pass mark used everywhere else in the feature.
  const tone = pct >= 75 ? "#15803d" : pct >= 50 ? "#92400e" : "#b42318"
  // Dots first, then the score - the original left-to-right order. Only the
  // score itself changed: it leads with the percentage now, because "1/15" and
  // "3/44" are not comparable at a glance whereas 7% and 7% are.
  return (
    <div className="d-flex align-items-center gap-2">
      <span className="d-inline-flex align-items-center">{circles}</span>
      <div className="text-nowrap">
        <div className="fw-bold" style={{ fontSize: "20px", color: tone }}>
          {pct}%
        </div>
        <div className="text-muted font-size-12">
          {correct}/{total}
        </div>
        {awaiting > 0 && (
          <div className="text-muted font-size-11">
            +{awaiting} awaiting marking
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The blocks of one strand, as the single row a tutor reads.
 *
 * A Year 1 English test puts eight topic blocks inside Grammar, so listed flat they are eight
 * rows differing only by a topic name the English UI does not otherwise show.
 *
 * KEYED ON STRAND **AND** `is_prereq`. A prerequisite test is a different sitting, earned by a
 * wrong answer rather than chosen; folding its blocks in with the base test's would add up two
 * tests into one score. They come back as two rows, each saying which it is - the same split
 * the KPI strip makes between "Base test" and "Overall".
 */
type StrandGroup = {
  key: string
  strandName: string
  isPrereq: boolean
  levelName: string
  attemptedOn: string | null
  correct: number
  total: number
  awaiting: number
  blocks: BmdAttemptHistoryRow[]
}

function groupByStrand(rows: BmdAttemptHistoryRow[]): StrandGroup[] {
  const groups = new Map<string, StrandGroup>()
  for (const row of rows) {
    const strandName = row.strand_name || row.topic_name
    const isPrereq = !!row.is_prereq
    const key = `${row.strand_id ?? strandName}|${isPrereq}`
    let group = groups.get(key)
    if (!group) {
      group = {
        key,
        strandName,
        isPrereq,
        levelName: row.level_name || "",
        attemptedOn: null,
        correct: 0,
        total: 0,
        awaiting: 0,
        blocks: [],
      }
      groups.set(key, group)
    }
    group.blocks.push(row)
    group.correct += row.correct_count
    group.total += row.total_count
    group.awaiting += row.awaiting_marking ?? 0
    // The strand was last worked on whenever its most recent block was.
    if (row.attempted_on && (!group.attemptedOn || row.attempted_on > group.attemptedOn)) {
      group.attemptedOn = row.attempted_on
    }
    // Blocks of one strand are normally one year; say nothing rather than pick one if not.
    if (group.levelName && row.level_name && row.level_name !== group.levelName) {
      group.levelName = ""
    }
  }
  return [...groups.values()]
}

/**
 * The statuses of a group's blocks, each named once.
 *
 * A count is only added where the status does NOT cover every block - "Completed" means all of
 * them, "Completed x6" means six of nine and the rest are elsewhere in the list.
 */
function groupPills(
  group: StrandGroup,
  copy: Pick<ReportCopy, "completedPill" | "splitCompletedPill">,
): (StatusPill & { count: number; ofAll: boolean })[] {
  const byLabel = new Map<string, StatusPill & { count: number; ofAll: boolean }>()
  for (const block of group.blocks) {
    for (const pill of statusPillsFor(block, copy)) {
      const held = byLabel.get(pill.label)
      if (held) held.count += 1
      else byLabel.set(pill.label, { ...pill, count: 1, ofAll: false })
    }
  }
  return [...byLabel.values()].map((pill) => ({
    ...pill,
    ofAll: pill.count === group.blocks.length,
  }))
}

/**
 * Whether a rolled-up strand row opens to show the topic blocks inside it.
 *
 * Set this to false and the Diagnostic Attempts table shows one row per strand and stops there:
 * no chevron, no child rows. Worth knowing before flipping it - Preview acts on a BLOCK, so
 * with the blocks hidden there is nothing left to preview from this table, and Chain falls back
 * to the strand's first block.
 *
 * Only affects a subject whose copy sets `attemptGrouping: "strand"`. Maths lists blocks
 * directly and is untouched either way.
 */
const STRAND_ROWS_EXPAND_TO_BLOCKS = true

/** One session topic block: the row this table has always had. */
const BlockRow: React.FC<{
  row: BmdAttemptHistoryRow
  copy: ReportCopy
  /** Indented and stripped of its year, when it sits under a strand row that already said it. */
  nested?: boolean
  onPreview?: (row: BmdAttemptHistoryRow) => void
  onViewChainAnalysis?: (row: BmdAttemptHistoryRow | null) => void
}> = ({ row, copy, nested = false, onPreview, onViewChainAnalysis }) => {
  const pills = statusPillsFor(row, copy)
  return (
    <tr className={nested ? "bg-light bg-opacity-50" : undefined}>
      <td className="font-size-13">{nested ? "" : row.level_name || "\u2014"}</td>
      <td
        className={nested ? "font-size-13" : "fw-semibold"}
        style={nested ? { paddingLeft: 38 } : undefined}
      >
        {plainText(row.topic_name)}
        {/* A prerequisite block was earned by a wrong answer, not chosen by a tutor, and it is
            usually a year below the test around it. Without this it reads as another assigned
            topic that went badly. A nested row's strand row has already said so. */}
        {row.is_prereq && !nested && (
          <span className="badge bg-light text-dark border fw-normal font-size-11 ms-2">
            Prerequisite test
          </span>
        )}
      </td>
      <td className="font-size-13">{formatDate(row.attempted_on)}</td>
      <td>
        <ScoreCircles
          correct={row.correct_count}
          total={row.total_count}
          awaiting={row.awaiting_marking}
        />
      </td>
      <td>
        <div className="d-flex flex-column align-items-start gap-1">
          {pills.map((p) => (
            <span
              key={p.label}
              className={"badge " + p.bgClass + " " + p.textClass + " font-size-11"}
            >
              {p.label}
            </span>
          ))}
        </div>
      </td>
      <td className="text-end">
        <div className="d-inline-flex gap-2 justify-content-end flex-wrap">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => onPreview && onPreview(row)}
          >
            <i className="fas fa-eye me-1"></i>
            Preview
          </button>
          {onViewChainAnalysis && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onViewChainAnalysis(row)}
              title="View chain analysis for this topic"
            >
              <i className="fas fa-link me-1"></i>
              Chain
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

/** A strand, and - unless STRAND_ROWS_EXPAND_TO_BLOCKS is off - the blocks inside it. */
const StrandRows: React.FC<{
  group: StrandGroup
  copy: ReportCopy
  expanded: boolean
  onToggle: () => void
  onPreview?: (row: BmdAttemptHistoryRow) => void
  onViewChainAnalysis?: (row: BmdAttemptHistoryRow | null) => void
}> = ({ group, copy, expanded, onToggle, onPreview, onViewChainAnalysis }) => {
  const pills = groupPills(group, copy)
  const open = STRAND_ROWS_EXPAND_TO_BLOCKS && expanded
  return (
    <>
      <tr>
        <td className="font-size-13">{group.levelName || "\u2014"}</td>
        <td className="fw-semibold">
          {STRAND_ROWS_EXPAND_TO_BLOCKS ? (
            <button
              type="button"
              className="btn btn-link p-0 me-2 text-decoration-none text-muted"
              onClick={onToggle}
              aria-expanded={open}
              title={open ? "Hide topics" : "Show topics"}
            >
              <i className={"fas fa-chevron-" + (open ? "down" : "right") + " font-size-11"}></i>
            </button>
          ) : null}
          {plainText(group.strandName)}
          {group.isPrereq && (
            <span className="badge bg-light text-dark border fw-normal font-size-11 ms-2">
              Prerequisite test
            </span>
          )}
          <span className="text-muted fw-normal font-size-12 ms-2">
            {group.blocks.length} topic{group.blocks.length === 1 ? "" : "s"}
          </span>
        </td>
        <td className="font-size-13">{formatDate(group.attemptedOn)}</td>
        <td>
          <ScoreCircles
            correct={group.correct}
            total={group.total}
            awaiting={group.awaiting}
          />
        </td>
        <td>
          <div className="d-flex flex-column align-items-start gap-1">
            {pills.map((p) => (
              <span
                key={p.label}
                className={"badge " + p.bgClass + " " + p.textClass + " font-size-11"}
              >
                {p.label}
                {p.ofAll ? "" : " \u00d7" + p.count}
              </span>
            ))}
          </div>
        </td>
        <td className="text-end">
          {/* No Preview: that opens one block, and this row is the whole strand. Chain hands
              over the strand's first block, which the Chain Analysis tab resolves to its
              cluster anyway - the same rung this row is. */}
          {onViewChainAnalysis && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onViewChainAnalysis(group.blocks[0])}
              title="View chain analysis for this strand"
            >
              <i className="fas fa-link me-1"></i>
              Chain
            </button>
          )}
        </td>
      </tr>
      {open &&
        group.blocks.map((row) => (
          <BlockRow
            key={row.pk_session_topic_key}
            row={row}
            copy={copy}
            nested
            onPreview={onPreview}
            onViewChainAnalysis={onViewChainAnalysis}
          />
        ))}
    </>
  )
}

export type BmdAttemptHistoryTableProps = {
  attempts: BmdAttemptHistoryRow[]
  onPreview?: (row: BmdAttemptHistoryRow) => void
  // When provided, the per-row "Chain" button and the header link become
  // active and hand off to the Chain Analysis tab. The row variant carries
  // topic context; the header variant opens the tab unscoped.
  onViewChainAnalysis?: (row: BmdAttemptHistoryRow | null) => void
}

export const BmdAttemptHistoryTable: React.FC<BmdAttemptHistoryTableProps> = ({
  attempts,
  onPreview,
  onViewChainAnalysis,
}) => {
  const copy = useReportCopy()
  // Which strand rows are open. Keyed by group, so a filter change does not close them.
  const [expandedStrands, setExpandedStrands] = React.useState<Set<string>>(
    () => new Set(),
  )
  const toggleStrand = React.useCallback((key: string) => {
    setExpandedStrands((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])
  const { dateOptions, bySessionKey } = React.useMemo(
    () => buildSessionIndex(attempts),
    [attempts],
  )
  const [selectedDate, setSelectedDate] = React.useState<string>(ALL_DATES)
  // 0 means "all tests on this date", otherwise it is the 1-based test index.
  // Only surfaces when the selected date has more than one test.
  const [selectedTestIndex, setSelectedTestIndex] = React.useState<number>(0)

  // If the selected date disappears on a year-level change, drop back to
  // "all" so the table does not go silently empty. Reset the test selector
  // whenever the date changes.
  React.useEffect(() => {
    if (
      selectedDate !== ALL_DATES &&
      !dateOptions.some((d) => d.date === selectedDate)
    ) {
      setSelectedDate(ALL_DATES)
    }
  }, [dateOptions, selectedDate])

  React.useEffect(() => {
    setSelectedTestIndex(0)
  }, [selectedDate])

  // How many tests live under the currently-selected date. Drives whether
  // the secondary "Test" dropdown appears.
  const testsForSelectedDate = React.useMemo(() => {
    if (selectedDate === ALL_DATES) return 0
    let n = 0
    for (const meta of bySessionKey.values()) {
      if (meta.dateLabel === selectedDate) n = Math.max(n, meta.testTotal)
    }
    return n
  }, [bySessionKey, selectedDate])

  const visible = attempts.filter((a) => {
    const meta = bySessionKey.get(a.fk_diagnostics_session_key)
    if (selectedDate !== ALL_DATES && meta?.dateLabel !== selectedDate) {
      return false
    }
    if (
      selectedDate !== ALL_DATES &&
      selectedTestIndex !== 0 &&
      meta?.testIndex !== selectedTestIndex
    ) {
      return false
    }
    return true
  })

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="text-uppercase text-muted font-size-12">
            Diagnostic Attempts
          </div>
          {dateOptions.length > 0 && (
            <div className="d-flex align-items-center">
              <label
                className="text-muted font-size-12 me-2 mb-0"
                htmlFor="bmd-session-filter"
              >
                Session:
              </label>
              <select
                id="bmd-session-filter"
                className="form-select form-select-sm"
                style={{ minWidth: 160 }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              >
                <option value={ALL_DATES}>All sessions</option>
                {dateOptions.map((d) => (
                  <option key={d.date} value={d.date}>
                    {d.date}
                  </option>
                ))}
              </select>
            </div>
          )}
          {testsForSelectedDate > 1 && (
            <div className="d-flex align-items-center">
              <label
                className="text-muted font-size-12 me-2 mb-0"
                htmlFor="bmd-test-filter"
              >
                Test:
              </label>
              <select
                id="bmd-test-filter"
                className="form-select form-select-sm"
                style={{ minWidth: 110 }}
                value={String(selectedTestIndex)}
                onChange={(e) =>
                  setSelectedTestIndex(Number(e.target.value) || 0)
                }
              >
                <option value="0">All</option>
                {Array.from(
                  { length: testsForSelectedDate },
                  (_, i) => i + 1,
                ).map((idx) => (
                  <option key={idx} value={idx}>
                    Test {idx}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <a
          href="#"
          className="font-size-12 text-primary"
          onClick={(e) => {
            e.preventDefault()
            if (onViewChainAnalysis) onViewChainAnalysis(null)
          }}
        >
          View chain analysis →
        </a>
      </div>

      {visible.length === 0 ? (
        <p className="text-muted mb-0">
          {attempts.length === 0
            ? "No diagnostic attempts yet."
            : "No attempts in the selected session."}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-borderless align-middle mb-0">
            <thead>
              <tr className="text-uppercase text-muted fw-bold font-size-12">
                <th style={{ width: "8%" }}>Year</th>
                <th style={{ width: "26%" }}>Topic</th>
                <th style={{ width: "14%" }}>Date</th>
                <th style={{ width: "18%" }}>Score</th>
                <th style={{ width: "16%" }}>Status</th>
                <th style={{ width: "18%" }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {copy.attemptGrouping === "strand"
                ? groupByStrand(visible).map((group) => (
                    <StrandRows
                      key={group.key}
                      group={group}
                      copy={copy}
                      expanded={expandedStrands.has(group.key)}
                      onToggle={() => toggleStrand(group.key)}
                      onPreview={onPreview}
                      onViewChainAnalysis={onViewChainAnalysis}
                    />
                  ))
                : visible.map((row) => (
                    <BlockRow
                      key={row.pk_session_topic_key}
                      row={row}
                      copy={copy}
                      onPreview={onPreview}
                      onViewChainAnalysis={onViewChainAnalysis}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BmdAttemptHistoryTable
